import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import Label from "../../../Utils/FormInputs/Label";
import SelectInput from "../../../Utils/FormInputs/SelectInput";
import TextField from "../../../Utils/FormInputs/TextField";
import { ResizableTextarea } from "../../../Utils/FormInputs/Textarea";
import {
    PGFormGrid,
    PGHint,
    PGActions,
    PGDangerBtn,
} from "./PartographStyles";

/* ═══════════════════════════════════════════════════════════════
   PartographEntryForm — Data entry form for each partograph
   time point.
   ═══════════════════════════════════════════════════════════════ */

export const PartographEntryForm = function(props) {
    const { pg } = props;
    const form = pg.entryForm;
    const handleChange = pg.handleEntryChange;
    const isEditing = !!pg.editingEntry;

    /* ─── Computed hints ─── */
    const fhr = parseFloat(form.fetal_heart_rate);
    const fhrWarn = !isNaN(fhr) && (fhr < 110 || fhr > 160);
    const dilation = parseFloat(form.cervical_dilation_cm);
    const dilWarn = !isNaN(dilation) && dilation > 10;
    const bpSys = parseFloat(form.maternal_bp_systolic);
    const bpDia = parseFloat(form.maternal_bp_diastolic);
    const bpWarn = (!isNaN(bpSys) && bpSys >= 140) || (!isNaN(bpDia) && bpDia >= 90);
    const temp = parseFloat(form.maternal_temperature);
    const tempWarn = !isNaN(temp) && temp >= 38;
    const mould = parseInt(form.moulding);
    const mouldWarn = !isNaN(mould) && mould >= 2;

    /* ─── Hours since start display ─── */
    const hoursSince = pg.computeHoursSinceStart(form.recorded_at);

    return (
        <div>
            {/* ─── Editing indicator ─── */}
            {isEditing && (
                <div style={{
                    padding: "8px 14px",
                    background: "#fff3e0",
                    border: "1px solid #ffcc80",
                    borderRadius: "6px",
                    marginBottom: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#e65100",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                }}>
                    Editing entry at {hoursSince.toFixed(1)}h
                    <button
                        onClick={pg.cancelEdit}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#c62828",
                            cursor: "pointer",
                            fontSize: "11px",
                            fontWeight: 600,
                            textDecoration: "underline",
                            padding: 0,
                            marginLeft: "auto",
                        }}
                    >
                        Cancel
                    </button>
                </div>
            )}

            {/* ═══ Time & Cervical Progress ═══ */}
            <PGFormGrid columns={3}>
                <Form.Group>
                    <Label name="Recorded At" type />
                    <TextField
                        type="datetime-local"
                        name="recorded_at"
                        value={form.recorded_at || ""}
                        onChange={handleChange}
                    />
                    <PGHint>{hoursSince >= 0 ? hoursSince.toFixed(1) + " hours since start" : ""}</PGHint>
                </Form.Group>

                <Form.Group>
                    <Label name="Cervical Dilation (cm)" type />
                    <TextField
                        type="number"
                        name="cervical_dilation_cm"
                        value={form.cervical_dilation_cm || ""}
                        onChange={handleChange}
                        placeholder="0 — 10"
                        min={0}
                        max={10}
                        step={0.5}
                    />
                    {dilWarn && <PGHint warn>Value exceeds 10cm</PGHint>}
                </Form.Group>

                <Form.Group>
                    <Label name="Descent (fifths palpable)" />
                    <SelectInput
                        name="descent_fifths"
                        value={form.descent_fifths || ""}
                        onChange={handleChange}
                        defaultOption="Select..."
                        options={[
                            { value: "5", text: "5/5 — Free (above brim)" },
                            { value: "4", text: "4/5 — Sinciput high" },
                            { value: "3", text: "3/5 — Sinciput at brim" },
                            { value: "2", text: "2/5 — Sinciput below brim" },
                            { value: "1", text: "1/5 — Almost fully descended" },
                            { value: "0", text: "0/5 — Fully descended" },
                        ]}
                    />
                </Form.Group>
            </PGFormGrid>

            {/* ═══ Fetal Assessment ═══ */}
            <PGFormGrid columns={3}>
                <Form.Group>
                    <Label name="Fetal Heart Rate (bpm)" type />
                    <TextField
                        type="number"
                        name="fetal_heart_rate"
                        value={form.fetal_heart_rate || ""}
                        onChange={handleChange}
                        placeholder="110 — 160"
                        min={50}
                        max={220}
                    />
                    {fhrWarn
                        ? <PGHint warn>{fhr < 110 ? "Bradycardia — below 110 bpm" : "Tachycardia — above 160 bpm"}</PGHint>
                        : <PGHint>Normal: 110-160 bpm</PGHint>
                    }
                </Form.Group>

                <Form.Group>
                    <Label name="Amniotic Fluid" />
                    <SelectInput
                        name="amniotic_fluid"
                        value={form.amniotic_fluid || ""}
                        onChange={handleChange}
                        defaultOption="Select..."
                        options={pg.AMNIOTIC_FLUID.map(o => ({ value: o.value, text: o.label }))}
                    />
                </Form.Group>

                <Form.Group>
                    <Label name="Moulding" />
                    <SelectInput
                        name="moulding"
                        value={form.moulding || ""}
                        onChange={handleChange}
                        defaultOption="Select..."
                        options={pg.MOULDING_OPTIONS.map(o => ({ value: o.value, text: o.label }))}
                    />
                    {mouldWarn && (
                        <PGHint warn>
                            {mould >= 3 ? "Severe — irreducible overlap" : "Significant — overlapping"}
                        </PGHint>
                    )}
                </Form.Group>
            </PGFormGrid>

            {/* ═══ Contractions ═══ */}
            <PGFormGrid columns={2}>
                <Form.Group>
                    <Label name="Contractions per 10 min" />
                    <TextField
                        type="number"
                        name="contractions_per_10min"
                        value={form.contractions_per_10min || ""}
                        onChange={handleChange}
                        placeholder="0 — 5+"
                        min={0}
                        max={10}
                    />
                </Form.Group>

                <Form.Group>
                    <Label name="Duration (seconds)" />
                    <TextField
                        type="number"
                        name="contraction_duration_seconds"
                        value={form.contraction_duration_seconds || ""}
                        onChange={handleChange}
                        placeholder="< 20, 20-40, > 40"
                        min={0}
                        max={120}
                    />
                    <PGHint>&lt;20s light, 20-40s moderate, &gt;40s strong</PGHint>
                </Form.Group>
            </PGFormGrid>

            {/* ═══ Maternal Vitals ═══ */}
            <PGFormGrid columns={4}>
                <Form.Group>
                    <Label name="BP Systolic (mmHg)" />
                    <TextField
                        type="number"
                        name="maternal_bp_systolic"
                        value={form.maternal_bp_systolic || ""}
                        onChange={handleChange}
                        placeholder="Systolic"
                        min={40}
                        max={250}
                    />
                    {!isNaN(bpSys) && bpSys >= 140 && (
                        <PGHint warn>{bpSys >= 160 ? "Severe hypertension!" : "Elevated"}</PGHint>
                    )}
                </Form.Group>

                <Form.Group>
                    <Label name="BP Diastolic (mmHg)" />
                    <TextField
                        type="number"
                        name="maternal_bp_diastolic"
                        value={form.maternal_bp_diastolic || ""}
                        onChange={handleChange}
                        placeholder="Diastolic"
                        min={20}
                        max={160}
                    />
                    {!isNaN(bpDia) && bpDia >= 90 && (
                        <PGHint warn>{bpDia >= 110 ? "Severe!" : "Elevated"}</PGHint>
                    )}
                </Form.Group>

                <Form.Group>
                    <Label name="Pulse (bpm)" />
                    <TextField
                        type="number"
                        name="maternal_pulse"
                        value={form.maternal_pulse || ""}
                        onChange={handleChange}
                        placeholder="60 — 100"
                    />
                </Form.Group>

                <Form.Group>
                    <Label name="Temperature (°C)" />
                    <TextField
                        type="number"
                        name="maternal_temperature"
                        value={form.maternal_temperature || ""}
                        onChange={handleChange}
                        placeholder="36.5 — 37.5"
                        min={34}
                        max={42}
                        step={0.1}
                    />
                    {tempWarn && (
                        <PGHint warn>{temp >= 39 ? "High fever!" : "Fever detected"}</PGHint>
                    )}
                </Form.Group>
            </PGFormGrid>

            {/* ═══ Urine Output ═══ */}
            <PGFormGrid columns={3}>
                <Form.Group>
                    <Label name="Urine Volume (mL)" />
                    <TextField
                        type="number"
                        name="urine_volume_ml"
                        value={form.urine_volume_ml || ""}
                        onChange={handleChange}
                        placeholder="Volume"
                        min={0}
                        max={2000}
                    />
                </Form.Group>

                <Form.Group>
                    <Label name="Protein" />
                    <SelectInput
                        name="urine_protein"
                        value={form.urine_protein || ""}
                        onChange={handleChange}
                        defaultOption="Select..."
                        options={pg.URINE_PROTEIN_OPTIONS.map(o => ({ value: o.value, text: o.label }))}
                    />
                </Form.Group>

                <Form.Group>
                    <Label name="Acetone" />
                    <SelectInput
                        name="urine_acetone"
                        value={form.urine_acetone || ""}
                        onChange={handleChange}
                        defaultOption="Select..."
                        options={pg.URINE_ACETONE_OPTIONS.map(o => ({ value: o.value, text: o.label }))}
                    />
                </Form.Group>
            </PGFormGrid>

            {/* ═══ Drugs & IV Fluids ═══ */}
            <PGFormGrid columns={3}>
                <Form.Group>
                    <Label name="Oxytocin (drops/min)" />
                    <TextField
                        type="number"
                        name="oxytocin_drops_per_min"
                        value={form.oxytocin_drops_per_min || ""}
                        onChange={handleChange}
                        placeholder="Drops/min"
                        min={0}
                        max={60}
                    />
                </Form.Group>

                <Form.Group>
                    <Label name="IV Fluids" />
                    <TextField
                        type="text"
                        name="iv_fluids"
                        value={form.iv_fluids || ""}
                        onChange={handleChange}
                        placeholder="e.g. RL 1L, NS 500mL"
                    />
                </Form.Group>

                <Form.Group>
                    <Label name="Other Drugs Given" />
                    <TextField
                        type="text"
                        name="drugs_given"
                        value={form.drugs_given || ""}
                        onChange={handleChange}
                        placeholder="e.g. MgSO4, Antibiotics"
                    />
                </Form.Group>
            </PGFormGrid>

            {/* ═══ Actions ═══ */}
            <PGActions>
                {isEditing && (
                    <PGDangerBtn onClick={pg.cancelEdit}>Cancel Edit</PGDangerBtn>
                )}
                <button className="btn sms-info-btn"
                    disabled={pg.saving}
                    onClick={() => isEditing ? pg.updateEntry() : pg.addEntry()}
                >
                    {pg.saving ? "Saving..." : isEditing ? "Update Entry" : "Record Entry"}
                </button>
            </PGActions>
        </div>
    );
};
